import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import Dropzone from "components/base/Dropzone";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useState } from "react";
import { Alert, Card } from "react-bootstrap";

const referenciaAnvisaBreadcrumbItems = [
  { label: "Home", url: "/" },
  { label: "Admin", url: "/admin" },
  { label: "Referência ANVISA", url: "/admin/referencia-anvisa", active: true },
];

interface ImportarReferenciaAnvisaResponse {
  totalLinhas: number;
  totalEansImportados: number;
}

const importarReferenciaAnvisa = async (
  file: File
): Promise<ImportarReferenciaAnvisaResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post("/api/referencia-anvisa/importar", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return data;
};

const ReferenciaAnvisa = () => {
  const [arquivo, setArquivo] = useState<File | undefined>();
  const { getMutationErrorHandler } = usePermissionModal();

  const mutation = useMutation({
    mutationFn: importarReferenciaAnvisa,
    onSuccess: () => setArquivo(undefined),
    onError: getMutationErrorHandler(
      "Você não possui permissão para importar a tabela CMED/ANVISA"
    ),
  });

  return (
    <div>
      <PageBreadcrumb items={referenciaAnvisaBreadcrumbItems} />

      <Card className="mt-3">
        <Card.Body>
          <h5 className="mb-1">Importar tabela CMED/ANVISA</h5>
          <p className="text-body-secondary small mb-4">
            Importa a planilha oficial da ANVISA (Câmara de Regulação do Mercado
            de Medicamentos). Usada pela revisão completa de catálogo e pelo
            autocomplete de produtos no PDV, que sugerem Departamento, Fabricante
            e Princípio Ativo a partir do código de barras. Reimportar uma
            versão mais recente atualiza os dados existentes.
          </p>

          <Dropzone
            onDrop={(acceptedFiles) => setArquivo(acceptedFiles[0])}
            accept={{
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            }}
          />

          {arquivo && (
            <div className="d-flex align-items-center gap-2 mt-3">
              <FontAwesomeIcon icon={faFileExcel} className="text-success" />
              <span className="small">{arquivo.name}</span>
            </div>
          )}

          {mutation.isSuccess && (
            <Alert variant="success" className="mt-3 mb-0">
              Importação concluída: {mutation.data.totalLinhas} linhas
              processadas, {mutation.data.totalEansImportados} EANs
              importados/atualizados.
            </Alert>
          )}

          <div className="mt-4">
            <Button
              variant="primary"
              disabled={!arquivo || mutation.isPending}
              loading={mutation.isPending}
              loadingPosition="start"
              onClick={() => arquivo && mutation.mutate(arquivo)}
            >
              Importar
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ReferenciaAnvisa;
